<template>
  <Button
    v-if="!isSupported"
    :isDark="themeIsDark"
    :title="$t('homeSlide')"
    @click.native="$emit('onSuccess')"
  />
  <div
    v-else
    class="w-full relative"
    :class="themeIsDark ? 'text-black' : 'text-white'"
  >
    <label
      class="
        absolute
        pointer-events-none
        text-base
        font-medium
        text-center
        mx-auto
        left-0
        right-0
        top-7
        md:top-6
      "
      >{{ $t("homeSlide") }}</label
    >
    <input
      type="range"
      v-model="slideVal"
      :min="slideMinVal"
      :max="slideMaxVal"
      class="slideButton bg-transparent"
      ref="slide"
    />
  </div>
</template>

<script lang="ts">
import { TranslationHandler } from '@/mixins/translation-handler'
import { Mixins, Component, Prop, Watch } from 'vue-property-decorator'
import { Getter } from 'vuex-class'
import Button from './Button.vue'

@Component({
  components: { Button }
})
export default class Slide extends Mixins(TranslationHandler) {
  @Getter themePrimaryColor!: string
  @Getter themeIsDark!: boolean

  @Prop() title!: string

  slideMinVal = 0
  slideMaxVal = 150
  slideVal = 0
  slideSpeed = 100
  rafId: any

  isSupported = !navigator.userAgent.includes('Firefox') // Firefox does not support this feature

  $refs!: {
    slide: HTMLInputElement
  }

  mounted () {
    if (!this.isSupported) {
      return
    }

    this.$refs.slide.addEventListener('mousedown', this.unlockStartHandler, false)
    this.$refs.slide.addEventListener('mousestart', this.unlockStartHandler, false)
    this.$refs.slide.addEventListener('mouseup', this.unlockEndHandler, false)
    this.$refs.slide.addEventListener('touchend', this.unlockEndHandler, false)
  }

  unlockStartHandler (event: Event) {
    // clear raf if trying again
    window.cancelAnimationFrame(this.rafId)
  }

  unlockEndHandler () {
    // determine if we have reached success or not
    if (this.slideVal >= this.slideMaxVal) {
      this.successHandler()
    } else {
      this.rafId = window.requestAnimationFrame(this.animateHandler)
    }
  }

  animateHandler () {
    // determine if we need to continue
    if (this.slideVal > -1) {
      window.requestAnimationFrame(this.animateHandler)
    }

    // decrement value
    this.slideVal = this.slideVal - this.slideSpeed
  }

  successHandler () {
    this.$emit('onSuccess')
    // reset input range
    this.$refs.slide.value = (0).toString()
  }

  @Watch('slideVal')
  onSlideValChange (val: number) {
    if (val >= this.slideMaxVal) {
      this.successHandler()
    }
  }
}
</script>

<style scoped lang="scss">
.slideButton {
  width: 100%;
  appearance: none;
}
.slideButton {
  &:active {
    &::-webkit-slider-thumb {
      appearance: none;
      transform: scale(1.1);
      cursor: grab;
    }
  }

  &::-webkit-slider-thumb {
    appearance: none;
    display: block;
    width: 45px;
    height: 45px;
    margin-top: 7px;
    margin-left: 7px;
    margin-right: 7px;
    border-radius: 50%;
    background-color: var(--primary-color);
    background-image: url("../assets/img/arrow.svg");
    background-position: center center;
    background-size: cover;
    transform-origin: 50% 50%;
    transform: scale(1);
    transition: transform ease-out 100ms;
    cursor: grab;
  }

  &:focus {
    outline: none;
  }

  &::-webkit-slider-runnable-track {
    height: 60px;
    padding-right: 14px;
    box-sizing: content-box;
    border-radius: 3rem;
    background-color: var(--primary-color);
  }
}

.slideButton::-ms-fill-lower,
.pullee::-ms-fill-upper {
  background-color: transparent;
}
.slideButton::-ms-tooltip {
  display: none;
}
</style>
